'use client';

import type { User } from '@api/models';
import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';

import { SlotsListSkeleton } from '@website/components/slot/SlotsListSkeleton';
import { useCurrentUser, useUserType } from '@website/hooks/useCurrentUser';

import type { Activity } from '../../../../../services/activities.service';
import { ViewSlotsButton } from './ViewSlotsButton';

interface ActivityOwnerFeaturesProps {
  activity: Activity;
  renderMode: 'menu-only' | 'actions-only';
}

interface ActionsProps {
  activity: Activity | null | undefined;
  user: User | null;
  status?: string;
}

interface MenuProps {
  activity: Activity;
}

/**
 * Shell for the activity action area.
 *
 * The owner toolbar (ActivityActionButtons — transitively pulls Stripe Connect
 * + react-day-picker + the create-slot dialog + the Activity bookings hook +
 * Luxon) and ActivityOwnerMenu (dropdown + tooltip + delete dialog) are heavy,
 * so they are dynamically imported ONLY for the activity's owning professional.
 *
 * Every other viewer — anonymous, learner, or a mentor who doesn't own this
 * activity — gets the lightweight, statically-bundled ViewSlotsButton ("Voir
 * les créneaux disponibles") so the booking CTA always renders and never drags
 * the owner payload onto the public detail page.
 *
 * Same pattern as LocationSectionShell + UserBookingHistory shell.
 */
export function ActivityOwnerFeatures({ activity, renderMode }: ActivityOwnerFeaturesProps) {
  const { data: user } = useCurrentUser();
  // Instant (synchronous, cookie-backed) hint — available on first paint,
  // before the async useCurrentUser() resolves. Lets a professional's slot
  // section start loading + show a skeleton immediately instead of flashing
  // empty. (It only tells us professional-vs-not, not ownership of THIS
  // activity, so it's used purely as an optimistic loading hint.)
  const likelyOwner = useUserType() === 'professional';

  const [OwnerMenu, setOwnerMenu] = useState<ComponentType<MenuProps> | null>(null);
  const [ActionButtons, setActionButtons] = useState<ComponentType<ActionsProps> | null>(null);

  // Owner menu only matters for the activity's owning professional. Gate the
  // dynamic import on that exact condition so non-owner authenticated users
  // skip the chunk too.
  const isOwner =
    !!user &&
    'isProfessional' in user &&
    (user as { isProfessional?: boolean }).isProfessional === true &&
    (user as { id?: string }).id === activity.professionalId;

  useEffect(() => {
    if (renderMode !== 'menu-only' || OwnerMenu || !isOwner) return;
    void import(/* webpackPrefetch: false, webpackPreload: false */ './ActivityOwnerMenu').then((m) =>
      setOwnerMenu(() => m.ActivityOwnerMenu)
    );
  }, [renderMode, OwnerMenu, isOwner]);

  // The owner toolbar is the only heavy chunk here, so gate its import strictly
  // on ownership. Non-owners never load it — they get the lightweight
  // ViewSlotsButton instead.
  useEffect(() => {
    if (renderMode !== 'actions-only' || ActionButtons || !isOwner) return;
    void import(/* webpackPrefetch: false, webpackPreload: false */ './ActivityActionButtons').then((m) =>
      setActionButtons(() => m.default)
    );
  }, [renderMode, ActionButtons, isOwner]);

  if (renderMode === 'menu-only') {
    if (!isOwner || !OwnerMenu) return null;
    return <OwnerMenu activity={activity} />;
  }

  // Owner → heavy toolbar (skeleton until the chunk resolves).
  if (isOwner) {
    return ActionButtons ? (
      <ActionButtons activity={activity} user={user as User} status={activity.status} />
    ) : (
      <SlotsListSkeleton />
    );
  }

  // We don't yet know who the viewer is (useCurrentUser still resolving) AND
  // the cookie hints they're a professional → they might be the owner, so show
  // the slot-list skeleton briefly instead of flashing the button.
  if (user === undefined && likelyOwner) {
    return <SlotsListSkeleton />;
  }

  // Anonymous, learner, or non-owner mentor → always show the booking CTA.
  return <ViewSlotsButton activity={activity} user={user ?? null} />;
}

/**
 * Skeleton kept for backwards compatibility; the activity Suspense fallbacks
 * use `null` now (see ActivityContent.tsx), so this is unused on the public
 * detail page.
 */
export function ActivityOwnerFeaturesSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-200 rounded-lg w-full" />
    </div>
  );
}
